/**
 * Conversion Rate Optimization label
 */

import Icon from '@/components/icons/Icon';
import { IconProps } from '@/components/icons/types';
import { classnames } from '@/helpers';
import { customTwMerge } from '@/helpers/theme';

type CroType = 'nextAvailableTime' | 'lowStock' | 'bought' | 'bookingWindow' | 'smt' | 'productDiscount';

export type CroLabelProps = {
  type: CroType;
  label: string;
  showBackground?: boolean;
  showIcon?: boolean;
  className?: string;
};

const iconMap: Record<CroType, (props: { size: IconProps['size'] }) => React.ReactNode> = {
  nextAvailableTime: (p) => <Icon variant="clock" color="fg_info_bold" {...p} />,
  lowStock: (p) => <Icon variant="low-stock" {...p} color="fg_notice_bold" />,
  bought: (p) => <Icon variant="users-alt" {...p} color="fg_brand" />,
  bookingWindow: (p) => <Icon variant="calendar-event" {...p} color="fg_info_bold" />,
  smt: (p) => <Icon variant="smt" {...p} color="fg_notice_bold" />,
  productDiscount: (p) => <Icon variant="product" {...p} color="fg_info_bold" />,
};

const getStyles = (type: CroType, showBackground: boolean) => {
  let baseClassname = classnames(
    'space-x-2xs inline-flex w-max items-center rounded-xs text-nowrap',
    showBackground ? 'text-l' : 'text-m',
    showBackground ? 'px-xs py-2xs' : 'py-3xs',
  );

  switch (type) {
    case 'nextAvailableTime':
    case 'bookingWindow':
      return classnames(baseClassname, 'text-fg_info_bold', showBackground && 'bg-bg_info_light');
    case 'lowStock':
      return classnames(baseClassname, 'text-fg_notice_bold', showBackground && 'bg-bg_notice');
    case 'bought':
      return classnames(baseClassname, 'text-fg_brand', showBackground && 'bg-bg_positive');
    case 'smt':
      return classnames(baseClassname, 'text-fg_notice_bold', showBackground && 'bg-bg_notice');
    case 'productDiscount':
      return classnames(baseClassname, 'text-fg_info_bold', showBackground && 'bg-bg_info_light');
    default:
      const _exhaustiveCheck: never = type;
      throw new Error(`Unhandled CroType : ${_exhaustiveCheck}`);
  }
};

const CroLabel = ({ type, label, showBackground = false, showIcon = false, className = '' }: CroLabelProps) => {
  return (
    <span className={customTwMerge(getStyles(type, showBackground), className)}>
      {showIcon && iconMap[type](showBackground ? { size: 'sm' } : { size: 'xs' })}
      <span className="overflow-hidden text-ellipsis text-inherit">{label}</span>
    </span>
  );
};

export default CroLabel;
